import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Brain, Activity, Zap, ArrowRight, CheckCircle2 } from "lucide-react";

const ServicesPage = () => {
  const services = [
    {
      icon: <Brain size={48} />,
      title: "AI-Driven Digital Marketing Strategy",
      tagline: "From Data to Direction",
      description: "Strategic planning using data intelligence, audience insights, channel prioritization, and AI-assisted decision making.",
      features: [
        "Comprehensive market and competitor analysis",
        "AI-powered audience segmentation and persona development",
        "Data-driven channel strategy and prioritization",
        "Performance forecasting and goal setting",
        "Strategic roadmap with clear milestones",
        "Monthly strategy reviews and optimization"
      ],
      image: "https://images.pexels.com/photos/17485657/pexels-photo-17485657.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      gradient: "from-brandPrimary/10 to-transparent"
    },
    {
      icon: <Activity size={48} />,
      title: "Brand Communication Strategy",
      tagline: "Clarity That Converts",
      description: "Crafting brand narratives, messaging frameworks, tone of voice, and content systems that create trust and recall.",
      features: [
        "Brand positioning and messaging architecture",
        "Tone of voice and communication guidelines",
        "Content strategy and editorial calendars",
        "Storytelling frameworks for consistent messaging",
        "Customer journey mapping and touchpoint optimization",
        "Brand audit and competitive differentiation"
      ],
      image: "https://images.unsplash.com/photo-1636755393526-a2249074de99?crop=entropy&cs=srgb&fm=jpg&q=85",
      gradient: "from-brandSecondary/10 to-transparent"
    },
    {
      icon: <Zap size={48} />,
      title: "AI Automation for Marketing Operations",
      tagline: "Scale Without Chaos",
      description: "Automating workflows, reporting, content systems, CRM touchpoints, and performance insights to scale efficiently.",
      features: [
        "Marketing automation setup and optimization",
        "AI-powered content generation workflows",
        "Automated reporting and performance dashboards",
        "CRM integration and lead nurturing automation",
        "Social media scheduling and optimization",
        "Process documentation and team training"
      ],
      image: "https://images.unsplash.com/photo-1675044794023-2c70962f4899?crop=entropy&cs=srgb&fm=jpg&q=85",
      gradient: "from-brandAccent/10 to-transparent"
    }
  ];

  return (
    <div className="min-h-screen noise-texture pt-32">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-brandSecondary font-medium text-sm uppercase tracking-wider" data-testid="services-label">
              Our Services
            </span>
            <h1 className="font-heading font-bold text-5xl md:text-6xl text-white mt-4 mb-6" data-testid="services-heading">
              Three Pillars of
              <br />
              <span className="text-gradient">Strategic Growth</span>
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed" data-testid="services-intro">
              Business solutions, not service lists. Each offering is designed to solve specific growth challenges 
              faced by modern brands.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto space-y-32">
          {services.map((service, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid={`service-detail-${idx}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                idx % 2 === 1 ? 'lg:grid-flow-dense' : ''
              }`}
            >
              {/* Image */}
              <div className={idx % 2 === 1 ? 'lg:col-start-2' : ''}>
                <div className="relative group">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-3xl shadow-2xl transition-transform group-hover:scale-[1.02]"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${service.gradient} rounded-3xl`}></div>
                </div>
              </div>

              {/* Content */}
              <div className={`space-y-6 ${idx % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <div className="text-brandPrimary">
                  {service.icon}
                </div>
                
                <div>
                  <h2 className="font-heading font-bold text-4xl text-white mb-2">
                    {service.title}
                  </h2>
                  <p className="text-brandSecondary font-medium text-lg">
                    {service.tagline}
                  </p>
                </div>

                <p className="text-gray-400 text-lg leading-relaxed">
                  {service.description}
                </p>

                <div className="space-y-3 pt-4">
                  {service.features.map((feature, featureIdx) => (
                    <div key={featureIdx} data-testid={`service-${idx}-feature-${featureIdx}`} className="flex items-start gap-3">
                      <CheckCircle2 size={20} className="text-brandSecondary flex-shrink-0 mt-1" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/contact"
                  data-testid={`service-${idx}-cta`}
                  className="inline-flex items-center gap-2 text-brandPrimary hover:text-brandPrimary/80 font-medium pt-4 group"
                >
                  Discuss This Service
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6 bg-darkPaper/50" data-testid="process-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4" data-testid="process-heading">
              How We Work
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-testid="process-subheading">
              A structured, transparent approach to strategic growth
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Discovery", description: "Deep dive into your business, goals, and current marketing state" },
              { step: "02", title: "Strategy", description: "Develop data-driven roadmap with clear objectives and KPIs" },
              { step: "03", title: "Execution", description: "Implement tactics with AI automation and human oversight" },
              { step: "04", title: "Optimization", description: "Continuous measurement, learning, and strategic refinement" }
            ].map((phase, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                data-testid={`process-step-${idx}`}
                className="glass-card rounded-2xl p-6 text-center"
              >
                <div className="font-heading font-bold text-5xl text-brandPrimary/30 mb-4">
                  {phase.step}
                </div>
                <h3 className="font-heading font-semibold text-xl text-white mb-3">
                  {phase.title}
                </h3>
                <p className="text-gray-400 text-sm">
                  {phase.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-3xl p-12 text-center"
          >
            <h2 className="font-heading font-bold text-4xl text-white mb-6" data-testid="services-cta-heading">
              Ready to Build Your Strategy?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto" data-testid="services-cta-description">
              Let's discuss which services align with your growth goals and craft a custom roadmap.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="inline-flex items-center gap-2 bg-brandPrimary hover:bg-brandPrimary/90 text-white px-10 py-5 rounded-full font-medium text-lg shadow-lg hover:shadow-brandPrimary/25 transition-all"
            >
              Book Your Strategy Call
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
